(() => {
  const query = `query WebflowFeaturedSparks($handle: String!) {
    collection(handle: $handle) {
      products(first: 8) {
        nodes {
          title
          description
          onlineStoreUrl
          tags
          featuredImage {
            url(transform: { maxWidth: 900, preferredContentType: WEBP })
            altText
          }
          sparkFamily: metafield(namespace: "custom", key: "spark_family") {
            reference {
              ... on Metaobject {
                name: field(key: "name") { value }
                shortDescription: field(key: "short_description") { value }
                grade: field(key: "grade") { value }
                grades: field(key: "grades") { value }
                lessonTimeMin: field(key: "lesson_time_min") { value }
                lessonTimeMax: field(key: "lesson_time_max") { value }
                catalogueImage: field(key: "catalogue_image") {
                  reference {
                    ... on MediaImage {
                      image {
                        url(transform: { maxWidth: 900, preferredContentType: WEBP })
                        altText
                      }
                    }
                  }
                }
                conceptCards: field(key: "concept_cards") {
                  references(first: 6) {
                    nodes {
                      ... on Metaobject {
                        name: field(key: "name") { value }
                      }
                    }
                  }
                }
                singlePackProduct: field(key: "single_pack_product") {
                  reference {
                    ... on Product { onlineStoreUrl }
                  }
                }
              }
            }
          }
          bundleFamily: metafield(namespace: "custom", key: "bundle_family") {
            reference {
              ... on Metaobject {
                name: field(key: "name") { value }
                shortDescription: field(key: "short_description") { value }
                grade: field(key: "grade") { value }
                grades: field(key: "grades") { value }
                lessonTimeMin: field(key: "lesson_time_min") { value }
                lessonTimeMax: field(key: "lesson_time_max") { value }
                catalogueImage: field(key: "catalogue_image") {
                  reference {
                    ... on MediaImage {
                      image {
                        url(transform: { maxWidth: 900, preferredContentType: WEBP })
                        altText
                      }
                    }
                  }
                }
                conceptCards: field(key: "concept_cards") {
                  references(first: 6) {
                    nodes {
                      ... on Metaobject {
                        name: field(key: "name") { value }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }`;
  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[char]));
  const trimText = (value) => escapeHtml(String(value || '').replace(/\s+/g, ' ').trim().slice(0, 132));
  const stripTagPrefix = (tag) => String(tag || '').replace(/^[^:]+:\s*/, '').trim();

  function fieldValue(metaobject, key) {
    return (((metaobject || {})[key] || {}).value || '').trim();
  }

  function cleanHandle(value) {
    const handle = String(value || '').trim();

    if (!handle || handle.startsWith('{{')) return '';
    return handle;
  }

  function hideSection(root) {
    const section = root.closest('section');
    (section || root).hidden = true;
  }

  function parseList(value) {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [String(parsed)];
    } catch (error) {
      return String(value).split(',').map((item) => item.trim()).filter(Boolean);
    }
  }

  function formatGrade(gradeValues) {
    const gradeText = gradeValues.join(' ').toLowerCase();
    const normalized = ` ${gradeText} `;
    const hasAllAges = gradeText.includes('all age');
    const flags = {
      prek: gradeText.includes('pre'),
      kindergarten: gradeText.includes('kindergarten'),
      first: gradeText.includes('1st') || gradeText.includes('grade 1') || normalized.includes(' 1 ') || gradeText.includes('1-5'),
      second: gradeText.includes('2nd') || gradeText.includes('grade 2') || normalized.includes(' 2 ') || gradeText.includes('1-5'),
      third: gradeText.includes('3rd') || gradeText.includes('grade 3') || normalized.includes(' 3 ') || gradeText.includes('1-5'),
      fourth: gradeText.includes('4th') || gradeText.includes('grade 4') || normalized.includes(' 4 ') || gradeText.includes('1-5'),
      fifth: gradeText.includes('5th') || gradeText.includes('grade 5') || normalized.includes(' 5 ') || gradeText.includes('1-5'),
    };
    const order = [
      ['prek', 'Pre-K', 'Pre-K'],
      ['kindergarten', 'K', 'Kindergarten'],
      ['first', '1st', '1st Grade'],
      ['second', '2nd', '2nd Grade'],
      ['third', '3rd', '3rd Grade'],
      ['fourth', '4th', '4th Grade'],
      ['fifth', '5th', '5th Grade'],
    ];
    const first = order.find(([key]) => flags[key]);
    const last = [...order].reverse().find(([key]) => flags[key]);

    if (hasAllAges) return 'All Ages';
    if (!first || !last) return '';
    if (first[2] === last[2]) return first[2];

    return `${first[1]}-${last[2]}`;
  }

  function formatLessonTime(min, max) {
    const minValue = Number(min || 0);
    const maxValue = Number(max || 0);

    if (!minValue || !maxValue) return '';
    return minValue === maxValue ? `${minValue} min` : `${minValue}-${maxValue} min`;
  }

  function conceptNames(family, tags) {
    const concepts = (((((family || {}).conceptCards || {}).references || {}).nodes || []))
      .map((concept) => fieldValue(concept, 'name'))
      .filter(Boolean);

    return concepts.length
      ? concepts
      : (tags || []).filter((tag) => /^Concept:/i.test(tag)).map(stripTagPrefix);
  }

  function cardFromProduct(product) {
    const sparkFamily = (((product.sparkFamily || {}).reference) || {});
    const bundleFamily = (((product.bundleFamily || {}).reference) || {});
    const family = Object.keys(sparkFamily).length ? sparkFamily : bundleFamily;
    const familyImage = (((family.catalogueImage || {}).reference || {}).image) || {};
    const productImage = product.featuredImage || {};
    const gradeValues = parseList(fieldValue(family, 'grade') || fieldValue(family, 'grades'));
    const tagGrade = (product.tags || []).find((tag) => /^Grade:/i.test(tag));
    const concepts = conceptNames(family, product.tags);

    return {
      title: product.title,
      description: fieldValue(family, 'shortDescription') || product.description,
      url: product.onlineStoreUrl,
      image: productImage.url ? productImage : familyImage,
      grade: formatGrade(gradeValues) || (tagGrade ? stripTagPrefix(tagGrade) : ''),
      lessonTime: formatLessonTime(fieldValue(family, 'lessonTimeMin'), fieldValue(family, 'lessonTimeMax')),
      concepts,
    };
  }

  function buildMeta(card) {
    const chips = [];

    if (card.grade) {
      chips.push(`<span class="fm-shopify-spark-tag fm-shopify-spark-tag--grade">${escapeHtml(card.grade)}</span>`);
    }

    return chips.length
      ? `<span class="fm-shopify-spark-meta" aria-label="Spark details">${chips.join('')}</span>`
      : '';
  }

  async function hydrate(root) {
    const track = root.querySelector('.fm-shopify-sparks-track');
    if (!track) return;

    try {
      const collectionHandle = cleanHandle(root.dataset.collectionHandle);

      if (!collectionHandle) {
        hideSection(root);
        return;
      }

      const headers = { 'content-type': 'application/json' };
      const storefrontToken = (root.dataset.storefrontToken || '').trim();

      if (storefrontToken) {
        headers['X-Shopify-Storefront-Access-Token'] = storefrontToken;
      }

      root.dataset.collectionHandle = collectionHandle;

      const response = await fetch(root.dataset.endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, variables: { handle: collectionHandle } }),
      });
      const json = await response.json();
      const products = (((json.data || {}).collection || {}).products || {}).nodes || [];
      const cards = products
        .filter((product) => product && product.title && product.onlineStoreUrl)
        .map(cardFromProduct);

      if (!cards.length) {
        hideSection(root);
        return;
      }

      track.innerHTML = cards.map((card) => {
        const image = card.image || {};
        const media = image.url
          ? `<img loading="lazy" src="${escapeHtml(image.url)}" alt="${escapeHtml(image.altText || card.title)}">`
          : `<span class="fm-shopify-spark-placeholder">${escapeHtml(card.title)}</span>`;

        return `<a class="fm-shopify-spark-card" role="listitem" href="${escapeHtml(card.url)}"><span class="fm-shopify-spark-inner"><span class="fm-shopify-spark-thumb">${media}</span><span class="fm-shopify-spark-body"><span class="fm-shopify-spark-title">${escapeHtml(card.title)}</span><span class="fm-shopify-spark-text">${trimText(card.description)}</span>${buildMeta(card)}</span></span></a>`;
      }).join('');

      root.querySelectorAll('.fm-shopify-sparks-nav').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          track.scrollBy({ left: button.classList.contains('next') ? 320 : -320, behavior: 'smooth' });
        });
      });
    } catch (error) {
      hideSection(root);
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-fm-shopify-sparks]').forEach(hydrate);
  });
})();
